import React, { useEffect, useState } from "react";
import styles from "./Sidebar.module.scss";
import links from "./links";
import { Link, useLocation } from "react-router-dom";
import { ROLES } from "../../constants/roles";
import { PATHS } from "../../constants/paths";
import { IconArrowLeft } from "@tabler/icons-react";
import { clearAdminViewSession } from "../../helpers/adminAccess";
import {
  AVATAR_ENABLED_CHANGED_EVENT,
  getAvatarEnabled,
} from "../../helpers/userProfileStorage";

const AVATAR_LINK_PATHS = new Set([
  PATHS.DOCTOR_AI_PRESCRIPTION_REVIEWS,
  PATHS.DOCTOR_AVATAR_CONVERSATIONS,
]);

const getRoleFromPathname = (pathname) => {
  if (pathname.startsWith("/receptionist")) return ROLES.RECEPTIONIST;
  if (pathname.startsWith("/doctor")) return ROLES.DOCTOR;
  if (pathname.startsWith("/ipd")) return ROLES.IPD_MANAGEMENT;
  if (pathname.startsWith("/counsellor")) return ROLES.COUNSELLOR;
  if (pathname.startsWith("/nurse")) return ROLES.NURSE;
  if (pathname.startsWith("/pharmacy") || pathname.startsWith("/pharmacist"))
    return ROLES.PHARMACIST;
  if (pathname.startsWith("/radiology") || pathname.startsWith("/radiologist"))
    return ROLES.RADIOLOGIST;
  if (pathname.startsWith("/lab-test")) return ROLES.LAB_TEST;
  if (pathname.startsWith("/therapist")) return ROLES.THERAPIST;
  if (pathname.startsWith("/yoga")) return ROLES.YOGA;
  if (pathname.startsWith("/billing-manager")) return ROLES.BILLING_MANAGER;
  if (pathname.startsWith("/dietician")) return ROLES.DIETICIAN;
  if (pathname.startsWith("/staff-manager")) return ROLES.STAFF_MANAGER;
  if (pathname.startsWith("/store-manager")) return ROLES.STORE_MANAGER;
  if (pathname.startsWith("/logistics")) return ROLES.LOGISTICS;
  if (pathname.startsWith("/pre-opd")) return ROLES.PRE_OPD_MANAGEMENT;
  if (pathname.startsWith("/follow-up")) return ROLES.FOLLOW_UP_MANAGEMENT;
  return ROLES.ADMIN;
};

const Sidebar = () => {
  const userRole = localStorage.getItem("user_role");
  const location = useLocation();
  const [avatarEnabled, setAvatarEnabled] = useState(
    () => getAvatarEnabled() === true,
  );

  useEffect(() => {
    const syncAvatarEnabled = () =>
      setAvatarEnabled(getAvatarEnabled() === true);
    window.addEventListener(AVATAR_ENABLED_CHANGED_EVENT, syncAvatarEnabled);
    return () =>
      window.removeEventListener(
        AVATAR_ENABLED_CHANGED_EVENT,
        syncAvatarEnabled,
      );
  }, []);

  const isAdmin =
    userRole === ROLES.ADMIN ||
    userRole === "admin" ||
    userRole === "hospital_admin";

  let effectiveRole = userRole;
  if (isAdmin) {
    effectiveRole = getRoleFromPathname(location.pathname);
  }

  let navLinks = links(effectiveRole);

  const selectedStaffName = sessionStorage.getItem("admin_selected_staff_name");
  const selectedStaffId = sessionStorage.getItem("admin_selected_staff_id");

  const handleBackToAdmin = () => {
    clearAdminViewSession();
  };

  // If admin is browsing another stakeholder section, prepend link back to Admin Dashboard
  if (isAdmin && (effectiveRole !== ROLES.ADMIN || selectedStaffId)) {
    navLinks = [
      {
        label: "Back to Admin Dashboard",
        path: PATHS.ADMIN_DASHBOARD,
        activePaths: [PATHS.ADMIN_DASHBOARD],
        svg: <IconArrowLeft size={18} stroke={2.2} />,
        onClick: handleBackToAdmin,
        isBackButton: true,
      },
      ...navLinks,
    ];
  }

  if (!avatarEnabled) {
    navLinks = navLinks.filter((link) => !AVATAR_LINK_PATHS.has(link.path));
  }

  return (
    <nav className={styles.subheader} aria-label="Primary">
      <div className={styles.links_container}>
        {navLinks.map((link, i) => {
          const prefixActive =
            link.activePathPrefixes?.some(
              (prefix) =>
                location.pathname === prefix ||
                location.pathname.startsWith(`${prefix}/`),
            ) ?? false;
          const isActive =
            link.activePaths?.includes(location.pathname) || prefixActive;

          return (
            <Link
              to={link.path}
              className={`${styles.navlink} ${
                isActive ? styles.active_navlink : ""
              } ${link.isBackButton ? styles.back_admin_btn : ""}`}
              key={link.path || i}
              aria-current={isActive ? "page" : undefined}
              onClick={link.onClick}
            >
              {link.svg}
              <p>{link.label}</p>
            </Link>
          );
        })}
      </div>
      {isAdmin && selectedStaffName && (
        <div className={styles.admin_viewing_badge}>
          <span className={styles.badge_dot} />
          <span>
            Viewing: <strong>{selectedStaffName}</strong> (Read-Only)
          </span>
        </div>
      )}
    </nav>
  );
};

export default Sidebar;
